import type { ItemFieldRow } from './item-field-reader';
import { phpJsonInteger } from './php-integer-arithmetic';
import { phpJsonString } from './php-json-values';
import { isPhpNumeric } from './php-decimal-values';
import { layerReadView } from './layer-read-view';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const utf8 = (value: string): number[] => [...encodeURIComponent(value).matchAll(/%([0-9A-F]{2})|([^%])/g)]
    .map(match => match[1] ? parseInt(match[1], 16) : match[2]!.charCodeAt(0));
const own = (row: unknown, key: string): unknown => {
    if (arrayLike(row)) return Object.prototype.propertyIsEnumerable.call(row, key) ? (row as Readonly<Record<string, unknown>>)[key] : undefined;
    if (typeof row === 'string' && /^[\t\n\r\v\f ]*[+-]?\d+[\t\n\r\v\f ]*$/.test(key)) {
        const offset = Number(key), bytes = utf8(row);
        const byte = bytes[offset < 0 ? bytes.length + offset : offset];
        return byte === undefined ? undefined : String.fromCharCode(byte);
    }
    return undefined;
};
const senses = new Set(['nightsight', 'fiendsight', 'blindsight', 'ghostsight', 'spellsight', 'truesight', 'tremorsense', 'telepathy', 'windsense']);
export class IntegerComputeSlotWriteError extends Error {}
export class IntegerComputeSlotNonJsonError extends Error {}

function stringArgument(value: unknown): string {
    if (value == null || arrayLike(value)) throw new TypeError('Integer layer name requires a scalar string');
    return phpJsonString(value);
}
function integerArgument(value: unknown): number {
    if (value == null || arrayLike(value) || !(['number', 'boolean'].includes(typeof value) || isPhpNumeric(value))) {
        throw new TypeError('Integer slot value requires a coercible integer');
    }
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric < -9223372036854775808 || numeric >= 9223372036854775808) {
        // An exact decimal integer string may denote PHP_INT_MAX even though its
        // JSON number projection rounds upward. Keep that typed-input distinction.
        if (typeof value !== 'string' || !/^[\t\n\r\v\f ]*[+-]?\d+[\t\n\r\v\f ]*$/.test(value)
            || BigInt(value.trim()) < -9223372036854775808n || BigInt(value.trim()) > 9223372036854775807n) {
            throw new TypeError('Integer slot argument is outside PHP integer range');
        }
    }
    return Number(phpJsonInteger(value));
}
// A working row owns only its root and the paths it writes. Definitions and
// other untouched branches remain shared, including frozen catalog entries.
const ownedPaths = new WeakMap<object, WeakSet<object>>();
const frozenResults = new WeakMap<object, unknown>();
let passResults: WeakMap<object, unknown> | undefined;
/** One rollup's inputs are read-only; later callers may edit their returned row. */
export function withIntegerCompileCopyScope<T>(run: () => T): T {
    const previous = passResults;
    passResults ??= new WeakMap();
    try { return run(); } finally { passResults = previous; }
}
function working(value: ItemFieldRow): Record<string, unknown> {
    const out = Object.fromEntries(Object.entries(value));
    ownedPaths.set(out, new WeakSet([out]));
    return out;
}
function writableChild(root: object, row: Record<string, unknown>, key: string, child: ItemFieldRow): Record<string, unknown> {
    const owned = ownedPaths.get(root);
    if (!owned) return child as Record<string, unknown>;
    if (owned.has(child)) return child as Record<string, unknown>;
    const copy = Object.fromEntries(Object.entries(child));
    owned.add(copy);
    Object.defineProperty(row, key, { value: copy, enumerable: true, writable: true, configurable: true });
    return copy;
}
function result(value: unknown): unknown {
    if (!arrayLike(value)) return value;
    if (passResults?.has(value)) return passResults.get(value);
    if (frozenResults.has(value)) return frozenResults.get(value);
    const entries = Object.entries(value);
    const list = entries.every(([key], i) => key === String(i));
    let changed = list !== Array.isArray(value);
    const values = entries.map(([key, child]) => {
        const next = result(child); changed ||= next !== child; return [key, next] as const;
    });
    const out = changed ? list ? values.map(([, child]) => child) : Object.fromEntries(values) : value;
    // Only immutable inputs can outlive this pass in the normalization memo.
    if (Object.isFrozen(value) && entries.every(([, child]) => !arrayLike(child) || frozenResults.has(child))) {
        if (arrayLike(out)) Object.freeze(out);
        frozenResults.set(value, out);
    }
    passResults?.set(value, out);
    if (arrayLike(out)) passResults?.set(out, out);
    return out;
}
export function putIntegerCompilePath(root: Record<string, unknown>, path: string[], value: unknown): void {
    putCompilePath(root,path,value);
}
/** PHP string-offset reads yield one raw byte, including invalid UTF-8 bytes. */
export function putPhpByteCompilePath(root: Record<string,unknown>,path: string[],byte: number): void {
    putCompilePath(root,path,String.fromCharCode(byte),byte);
}
function putCompilePath(root: Record<string,unknown>,path: string[],value: unknown,rawByte?: number): void {
    let row = root;
    for (let i = 0; i < path.length - 1; i++) {
        const key = path[i]!;
        let child = own(row, key);
        if (child == null || child === false) { child = {}; Object.defineProperty(row, key, { value: child, enumerable: true, writable: true, configurable: true }); }
        if (!arrayLike(child)) {
            if (typeof child === 'string' && i === path.length - 2 && /^[\t\n\r\v\f ]*[+-]?\d+[\t\n\r\v\f ]*$/.test(path[i + 1]!)) {
                const bytes = utf8(child), offset = Number(path[i + 1]);
                const index = offset < 0 ? bytes.length + offset : offset;
                if (index < 0) return; // PHP warns and leaves an out-of-range negative offset unchanged.
                const out = new Uint8Array(Math.max(bytes.length, index + 1)); out.fill(32); out.set(bytes);
                const assigned = rawByte === undefined ? utf8(phpJsonString(value)) : [rawByte];
                if (!assigned.length) throw new IntegerComputeSlotWriteError('Cannot assign an empty string to a string offset');
                out[index] = assigned[0]!;
                let decoded: string;
                try { decoded = decodeURIComponent(Array.from(out, byte => `%${byte.toString(16).padStart(2, '0')}`).join('')); }
                catch { throw new IntegerComputeSlotNonJsonError('PHP string offset write produced non-UTF-8 JSON data'); }
                Object.defineProperty(row, key, { value: decoded, enumerable: true, writable: true, configurable: true });
                return;
            }
            if (typeof child === 'string') throw new TypeError('Cannot access offset of type string on string');
            throw new IntegerComputeSlotWriteError('Cannot use scalar value as an array');
        }
        row = writableChild(root, row, key, child);
    }
    if (rawByte !== undefined && rawByte >= 128) throw new IntegerComputeSlotNonJsonError('PHP byte read produced non-UTF-8 JSON data');
    Object.defineProperty(row, path[path.length - 1]!, { value, enumerable: true, writable: true, configurable: true });
}
export function removeIntegerCompilePath(root: Record<string, unknown>, path: string[]): void {
    let row = root;
    for (const key of path.slice(0, -1)) {
        const child = own(row, key);
        if (child == null || child === false) return;
        if (!arrayLike(child)) throw new IntegerComputeSlotWriteError('Cannot unset an offset in a non-array');
        row = writableChild(root, row, key, child);
    }
    delete (row as Record<string, unknown>)[path[path.length - 1]!];
}
function destination(layer: string): string[] {
    if (senses.has(layer)) return ['computed', 'creature', 'senses', layer, 'total'];
    if (layer === 'teleport' || layer.startsWith('speed_')) return ['computed', 'creature', 'speeds', layer === 'teleport' ? layer : layer.slice(6), 'total'];
    if (layer === 'reach' || layer === 'damage_mitigation') return ['computed', 'creature', layer, 'total'];
    return ['computed', 'creature', 'modifiers', layer];
}
/** Actual _ft_write_compute_int_layer destination and zero-elision rules.
 * Copies the changed path; source scoring and the full rollup are separate.
 */
export function writeIntegerComputeSlot(input: ItemFieldRow, rawLayer: string, rawValue: number): ItemFieldRow {
    if (!arrayLike(input)) throw new TypeError('Integer slot write requires a PHP array');
    const layer = stringArgument(rawLayer), value = integerArgument(rawValue), out = working(input) as Record<string, unknown>;
    writeIntegerComputeSlotValue(out,layer,value);
    return result(out) as ItemFieldRow;
}
/** Internal write on a working row; untouched branches remain read-only. */
export function writeIntegerComputeSlotValue(out: Record<string,unknown>,layer: string,value: number): void {
    const path = destination(layer);
    if (value === 0 && (layer === 'reach' || layer === 'damage_mitigation')) removeIntegerCompilePath(out, path.slice(0, -1));
    else if (value === 0 && path[2] === 'modifiers') { removeIntegerCompilePath(out, ['item_layer_types', layer]); removeIntegerCompilePath(out, path); }
    else putIntegerCompilePath(out, path, value);
}
export const integerCompileWorkingRow = (row: ItemFieldRow): Record<string,unknown> => working(row) as Record<string,unknown>;
/** Delta projection owns only paths it changes; the rest of the actor stays borrowed. */
export function integerCompileBorrowedRow(row: ItemFieldRow): Record<string, unknown> {
    const view = layerReadView(row);
    ownedPaths.set(view, new WeakSet([view]));
    return view;
}
export const integerCompileResult = (row: Record<string,unknown>): ItemFieldRow => result(row) as ItemFieldRow;
/** Stored compute/provenance slots, preserving absence for repeated-pass reconstruction. */
export function readIntegerCompilePath(input: ItemFieldRow, path: readonly string[]): unknown {
    let value: unknown = input;
    for (const key of path) value = own(value, key);
    return value;
}
/** Actual transitional read: ordinary families alone fall back to root[0]. */
export function readIntegerComputeSlot(input: ItemFieldRow, rawLayer: string): number {
    if (!arrayLike(input)) throw new TypeError('Integer slot read requires a PHP array');
    const layer = stringArgument(rawLayer), path = destination(layer);
    let value = readIntegerCompilePath(input, path);
    if (value == null && path[2] === 'modifiers') {
        const legacy = own(input, layer);
        value = typeof legacy === 'string' ? legacy[0] : own(legacy, '0');
    }
    return Number(phpJsonInteger(value ?? 0));
}
